import { matchPath } from 'react-router-dom';

/**
 * The route PATTERNS the corporate site reports, spelled exactly as the admin console's
 * routeLabels.ts keys them ("corporate:<pattern>"). The pattern is what is recorded,
 * never the real URL, so no report, blog or category id ever leaves the browser.
 *
 * Most specific first: '/report/categories' must win over '/report/:id'.
 * Pages a visitor sees before signing in are not in the list (see UNTRACKED_ROUTES):
 * nothing is recorded for a person who is not signed in.
 */
export const TRACKED_ROUTES = [
  '/',
  '/pricing',
  '/report/categories',
  '/report/category/:id',
  '/report/payment/:id',
  '/report/:id',
  '/success',
  '/success/signed-in',
  '/success/password-changed',
  '/success/sign-up-complete',
  '/success/payment-confirmed',
  '/success/subscription-active',
  '/success/invite-accepted',
  '/blogs',
  '/blog/:id',
  '/privacy',
  '/legal',
  '/about-us',
  '/terms',
  '/contact-us',
  '/workspace',
  '/team',
  '/career',
  '/faq',
  '/subscription/payment',
  '/change-plan',
  '/change-plan/invoice',
  '/profile',
] as const;

/**
 * Routes that exist in App.tsx but are deliberately not reported: sign-in and account
 * set-up pages, the error page, the dev test page and the catch-all redirect.
 * routePatterns.test.ts fails when a route is in neither list, so a new page cannot
 * silently go untracked.
 */
export const UNTRACKED_ROUTES = [
  '/login',
  '/sign-up',
  '/account-type',
  '/sign-up-payment',
  '/verify-email',
  '/forgot-password',
  '/reset-password',
  '/accept-invite',
  '/change-password-required',
  '/error',
  '/test',
  '*',
] as const;

/**
 * /success is one shared page that many flows navigate to, so each flow passes one of these
 * fixed words as `state.kind`. Only a word from this list is ever turned into a reported
 * pattern ('/success/<kind>'); nothing else from the navigation state can reach the tracker.
 * Keep in step with the '/success/<kind>' entries in TRACKED_ROUTES.
 */
export const SUCCESS_KINDS = [
  'signed-in',
  'password-changed',
  'sign-up-complete',
  'payment-confirmed',
  'subscription-active',
  'invite-accepted',
] as const;

export type SuccessKind = (typeof SUCCESS_KINDS)[number];

const isSuccessKind = (value: unknown): value is SuccessKind =>
  typeof value === 'string' && (SUCCESS_KINDS as readonly string[]).includes(value);

/** The `kind` a page put in its navigation state, or undefined when it is missing or not one of SUCCESS_KINDS. */
export function successKindFromState(state: unknown): SuccessKind | undefined {
  if (typeof state !== 'object' || state === null) return undefined;
  const kind = (state as { kind?: unknown }).kind;
  return isSuccessKind(kind) ? kind : undefined;
}

/** The pattern for a pathname, or null when the page is not tracked. Path only: pass no query string or hash. */
export function matchTrackedRoute(pathname: string): string | null {
  for (const pattern of TRACKED_ROUTES) {
    if (matchPath({ path: pattern, end: true }, pathname)) return pattern;
  }
  return null;
}

/**
 * The pattern to report for a pathname plus, for the shared /success page, the outcome it showed.
 * '/success' with a recognised kind becomes '/success/<kind>'; a missing or unrecognised kind keeps
 * plain '/success', and the kind is ignored on every other page.
 */
export function resolveTrackedRoute(pathname: string, kind?: unknown): string | null {
  const pattern = matchTrackedRoute(pathname);
  if (pattern === '/success' && isSuccessKind(kind)) return `/success/${kind}`;
  return pattern;
}
